import { Controller } from "@hotwired/stimulus"

// Keeps a panel current while something in the background finishes, without
// reloading the page (which would close everything the reader has open).
//
// The server decides when to stop: the fragment it returns only carries
// data-controller="panel-refresh" while there is still work in flight. When it
// doesn't, the replacement element never connects this controller and polling
// ends by itself.
//
// Usage:
//   <section id="pins" data-controller="panel-refresh"
//            data-panel-refresh-url-value="/some/fragment"
//            data-panel-refresh-interval-value="8000">
//
// Values:
//   url      — a fragment whose root element has the same id as this one.
//   interval — milliseconds between refreshes.
export default class extends Controller {
  static values = { url: String, interval: { type: Number, default: 8000 } }

  connect() {
    this.timer = setTimeout(() => this.refresh(), this.intervalValue)
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  async refresh() {
    try {
      const response = await fetch(this.urlValue, {
        headers:     { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const fragment = document.createRange().createContextualFragment(await response.text())
      const next     = fragment.getElementById(this.element.id)
      if (next) return this.element.replaceWith(next)
    } catch (error) {
      // A missed poll isn't worth interrupting anyone for; try again.
    }
    this.timer = setTimeout(() => this.refresh(), this.intervalValue)
  }
}
